import { Alert, AlertTitle } from "@mui/material";
import React, { ReactNode } from "react";
import { HealthCheck } from "../../../../controller/health-check";
import { PrimaryButton } from "../buttons";
import { Circular } from "../circular";
import { MultilineText } from "../multiline-text";

export namespace ErrorBoundary {
    /**
     * {@link ErrorBoundary} コンポーネントのプロパティ定義
     */
    export interface Props {
        /**
         * 子要素
         */
        readonly children?: ReactNode | undefined;
    }
}

export class ErrorBoundary extends React.Component<ErrorBoundary.Props, State> {
    state: State = {
        error: undefined,
        loading: false,
        onHealthChange: undefined,
    };

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        console.error("致命的なエラー", error, "in", errorInfo.componentStack);

        // 遅延読込のエラーだった場合、新しいデプロイが行われ、ファイル名が変化
        // している可能性がある。1回だけ自動的にリロードする。
        if (isLoadingError(error)) {
            if (HealthCheck.health === false) {
                const onHealthChange = () => {
                    if (HealthCheck.health) {
                        HealthCheck.removeEventListener(
                            "change",
                            onHealthChange,
                        );
                        reload();
                    }
                };
                this.setState({ onHealthChange });
                HealthCheck.addEventListener("change", onHealthChange);
            } else if (isAutoReloadedAlready(error)) {
                console.warn(
                    "This session has experienced an automatic reload already.",
                );
                this.setState({ error });
            } else {
                reload();
                this.setState({ loading: true });
            }
        } else {
            this.setState({ error });
        }
    }

    override componentWillUnmount(): void {
        const { onHealthChange } = this.state;

        if (onHealthChange) {
            HealthCheck.removeEventListener("change", onHealthChange);
        }
    }

    override render(): React.ReactNode {
        const { children } = this.props;
        const { error, loading, onHealthChange } = this.state;

        if (onHealthChange) {
            return (
                <Alert severity="warning">
                    <AlertTitle>
                        インターネットに接続されていません。
                    </AlertTitle>
                    サーバーからプログラムを取得するため、インターネットに接続してください。
                </Alert>
            );
        }

        if (error) {
            return (
                <Alert severity="error">
                    <AlertTitle>致命的なエラーが発生しました。</AlertTitle>
                    <MultilineText>{error.message}</MultilineText>
                    <br />
                    <PrimaryButton onClick={reload}>再表示する</PrimaryButton>
                </Alert>
            );
        }

        if (loading) {
            return <Circular />;
        }

        return children;
    }
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

interface State {
    error: Error | undefined;
    loading: boolean;
    onHealthChange: (() => void) | undefined;
}

const LastErrorMessage = "b7cf14d7-9d2c-4484-a78c-58f0f08feaf6";

/**
 * エラーが読み込みエラーかどうか判定します。
 *
 * TODO(mysticatea): とりあえず Chromium 系のブラウザであればこれで動作します。
 *                   別のブラウザで動作するかどうかの検証は、まだできていません。
 *
 * @param error エラー
 * @returns エラーが読み込みエラーであれば真
 */
function isLoadingError(error: unknown): error is TypeError {
    return (
        error instanceof TypeError &&
        error.message.startsWith("Failed to fetch dynamically imported module:")
    );
}

/**
 * 既に自動リロードされているかどうか判定します。
 *
 * この関数には副作用があります。
 * エラーメッセージをセッションストレージに保存し、その保存したエラーメッセージと
 * 今回のエラーメッセージが同じかどうか検証しています。
 *
 * @param error エラー
 * @returns 既に自動リロードされていたら真
 */
function isAutoReloadedAlready(error: TypeError): boolean {
    if (error.message === sessionStorage.getItem(LastErrorMessage)) {
        sessionStorage.removeItem(LastErrorMessage);
        return true;
    }

    sessionStorage.setItem(LastErrorMessage, error.message);
    return false;
}

function reload(): void {
    location.reload();
}
